import {ProviderLogo} from '@github-ui/copilot-byok/providers/ProviderLogo'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {useLocation} from '@github-ui/react-router'
import {CopilotIcon, SyncIcon, TriangleDownIcon} from '@primer/octicons-react'
import {
  ActionList,
  ActionMenu,
  ButtonGroup,
  ConfirmationDialog,
  IconButton,
  Link,
  Tooltip as PrimerTooltip,
  VisuallyHidden,
} from '@primer/react'
import {clsx} from 'clsx'
import React, {type ReactNode, useCallback, useEffect, useRef, useState} from 'react'

import {useOverlayDirection} from '../contexts/OverlayDirectionContext'
import {formatQuotaResetLabel} from '../hooks/use-quota-banner'
import {AUTO_MODEL_ID} from '../utils/auto-model'
import {
  ALMOST_OUT_THRESHOLD,
  APPROACHING_THRESHOLD,
  ENABLE_ADDITIONAL_REQUESTS_URL,
  FREE_QUOTA_TRIGGER,
  MANAGE_BILLING_URL,
  MANAGE_BUDGET_URL,
  PREMIUM_QUOTA_TRIGGER,
  UPGRADE_PLAN_URL,
  UPGRADE_PLAN_URL_NEW,
} from '../utils/copilot-chat-helpers'
import {getActiveMessages} from '../utils/copilot-chat-subthreading-helpers'
import {
  type CopilotChatMessage,
  type CopilotChatMode,
  type CopilotChatModel,
  type CopilotChatReference,
  CopilotLicenseType,
  CopilotPlan,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {copilotLocalStorage} from '../utils/copilot-local-storage'
import {useChatStateValues, useSafeChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {
  categorizeModels,
  type CustomModelGroup,
  getMinimumRequiredPlan,
  getOnlyEnabledModel,
  hasModelCategoryDiversity,
} from '../utils/model-picker-helpers'
import {BASE_MODEL_ID, getDefaultModel, isCustomModel} from '../utils/models'
import {ModelInfoPopover} from './ModelInfoPopover'
import styles from './ModelPicker.module.css'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from './PortalContainerUtils'
import {useEntitlement} from './quota/EntitlementContext'

/**
 * Formats a billing multiplier for display.
 * @param multiplier - The billing multiplier value
 * @returns A formatted string like "3x", "1.5x", "0.33x", or "0x" (default)
 */
function formatMultiplier(multiplier: number | undefined): string {
  // Default to 0 if no multiplier is defined
  const value = multiplier ?? 0

  // Convert to number to remove trailing zeros, then back to string
  return `${Number(value.toFixed(2))}x`
}

type ModelPickerType = 'global' | 'message-retry'
type Command = NewConversationDueToExternalContent | NewConversationDueToDegradedImageSupport

// This isn't 100% typesafe, but if this pattern continues to extend considering separating out command types into the shape of their actions and reasons as their own enums.
type NewConversationDueToExternalContent = {
  action: 'requires-new-conversation'
  reason: 'external-content'
}
type NewConversationDueToDegradedImageSupport = {
  action: 'requires-new-conversation'
  reason: 'degraded-image-support'
}

interface ModelPickerProps {
  onNewThreadSelected: () => Promise<void>
  type?: ModelPickerType
  selectedModel?: CopilotChatModel // defaults to the thread's model if unset
  onModelSelected?: (model: CopilotChatModel) => void
  disabled?: boolean
}

export function ModelPicker({
  onNewThreadSelected,
  type = 'global',
  selectedModel,
  onModelSelected,
  disabled = false,
}: ModelPickerProps) {
  const manager = useChatManager()

  const {
    model,
    availableModels,
    mode,
    messages,
    currentReferences,
    selectedThreadID,
    threads,
    modelsLoading,
    isWaitingOnCopilot,
    streamingMessage,
    autoModeDiscounts: globalAutoModeDiscounts,
  } = useChatStateValues(
    'model',
    'availableModels',
    'mode',
    'messages',
    'currentReferences',
    'selectedThreadID',
    'threads',
    'modelsLoading',
    'isWaitingOnCopilot',
    'streamingMessage',
    'autoModeDiscounts',
  )

  // The model pinned for the selected thread's Auto Mode routing, if any. Used to
  // reveal the underlying routed model on hover/focus of the Auto picker chip.
  const selectedThread = selectedThreadID ? threads.get(selectedThreadID) : undefined
  const autoPickedModel = selectedThread?.autoPickedModel
  const autoModeDiscounts = selectedThread?.autoModeDiscounts ?? globalAutoModeDiscounts
  const {premiumInteractionsQuotaExceeded, overagesEnabled, plan, licenseType, tokenBasedBillingEnabled} =
    useEntitlement()
  const modelsPending = modelsLoading.state === 'pending'
  const {search, pathname} = useLocation()
  const hasSelectedDefaultModel = useRef(false)

  const shouldShowFallbackModel =
    copilotFeatureFlags.premiumRequestQuotasEnabled && premiumInteractionsQuotaExceeded && !overagesEnabled

  let isDisabled = disabled
  // Check FF for disabling model picker while streaming
  if (copilotFeatureFlags.disableModelPickerWhileStreaming) {
    // Disable model picker when streaming is active
    const isStreaming = isWaitingOnCopilot || streamingMessage !== null
    isDisabled = disabled || isStreaming
  }

  const [pending, setPending] = useState<{
    model: CopilotChatModel
    command: Command
  } | null>(null)

  const modelsLoadedRef = useRef(false)

  if (!selectedModel) {
    // eslint-disable-next-line react-hooks/immutability
    selectedModel = model
  }

  useEffect(() => {
    if (modelsPending && licenseType !== CopilotLicenseType.Unlicensed) {
      void manager.fetchModels()
    }
  }, [manager, modelsPending, licenseType])

  // Reset the once-set default-selection guard whenever the available models list is replaced
  // (e.g. after a forced refetch on plan change). MODELS_LOADED is the only action that updates
  // `availableModels`, so this fires exactly when there is a new list to re-resolve against.
  // Declared before the select effect so the reset runs first in the same commit.
  useEffect(() => {
    hasSelectedDefaultModel.current = false
  }, [availableModels])

  useEffect(() => {
    // After models have loaded, select the right model. This is either (in order):
    // 1. the model last used for the thread, stored in local storage
    // 2. the default model if no model is stored in local storage
    // 3. the fallback model if none of the prior models are available because
    //    - they are premium models _and_
    //    - the user has exhausted their premium consumption limits _and_
    //    - the user has no additional premium request billing budget enabled
    if (!hasSelectedDefaultModel.current && !modelsPending && availableModels.length > 1) {
      const storedModel = copilotLocalStorage.getModel(selectedThreadID)
      const storedModelId = storedModel?.id

      // check if the thread's stored model is available
      let modelChoice =
        availableModels.find(
          m => m.id === storedModelId && (shouldShowFallbackModel || storedModelId !== BASE_MODEL_ID),
        ) || null
      if (
        // select the default model if the current model is not available
        (!modelChoice && plan) ||
        (plan !== CopilotPlan.IndividualFree &&
          modelChoice?.billing?.is_premium &&
          premiumInteractionsQuotaExceeded &&
          !overagesEnabled)
      ) {
        modelChoice = getDefaultModel(availableModels, plan, premiumInteractionsQuotaExceeded, overagesEnabled)
      }
      if (modelChoice) {
        const isNewThread = selectedThreadID === null
        manager.selectModel(modelChoice, true, isNewThread)
        hasSelectedDefaultModel.current = true
      }
    }
  }, [
    availableModels,
    manager,
    model,
    modelsPending,
    overagesEnabled,
    plan,
    premiumInteractionsQuotaExceeded,
    selectedThreadID,
    shouldShowFallbackModel,
  ])

  // Load model from URL if it exists
  useEffect(() => {
    if (mode !== 'immersive') return

    const urlSearchParams = new URLSearchParams(search)
    const queryModel = urlSearchParams.get('model')

    if (queryModel && (pathname === '/copilot' || pathname === '/copilot/')) {
      if (model.id === queryModel) return

      // Only run this logic when availableModels is populated for the first time
      if (modelsLoadedRef.current || !availableModels?.length) return

      modelsLoadedRef.current = true
      const availableModel = availableModels?.find(m => m.id === queryModel)
      if (availableModel) {
        void manager.selectModel(availableModel, false)
        sendEvent('dotcom_chat.activate', {target: 'COPILOT_MODEL_LOADED_FROM_URL', mode: 'immersive'})
      }
    }
  }, [availableModels, manager, mode, model?.id, pathname, search])

  function selectModel(m: CopilotChatModel): void {
    if (onModelSelected) {
      onModelSelected(m)
    } else {
      void manager.selectModel(m)
    }
  }

  // If you don't have at least two models, don't show the picker
  if (availableModels.length < 2) return null

  const onModelPicked = (newModel: CopilotChatModel) => {
    if (type === 'global' && isSameModel(selectedModel, newModel)) return

    sendEvent('dotcom_chat.activate', {
      target: 'MODEL_PICKER_MODEL_SELECT',
      mode: 'immersive',
      model: newModel.id,
      isUnchanged: isSameModel(selectedModel, newModel),
      type,
    })

    if (isSameModel(selectedModel, newModel)) {
      selectModel(newModel)
    } else if (
      modelSwitchingRequiresNewConversationDueToExternalContent(messages, newModel, type === 'message-retry')
    ) {
      setPending({model: newModel, command: {action: 'requires-new-conversation', reason: 'external-content'}})
    } else if (
      modelSwitchingRequiresNewConversationDueToDegradedImageSupport(
        messages,
        currentReferences,
        newModel,
        type === 'message-retry',
      )
    ) {
      setPending({model: newModel, command: {action: 'requires-new-conversation', reason: 'degraded-image-support'}})
    } else {
      selectModel(newModel)
    }
  }

  return (
    <>
      <ModelPickerDesign
        selectedModel={selectedModel}
        models={availableModels}
        type={type}
        onModelPicked={onModelPicked}
        disabled={isDisabled}
        variant={'invisible'}
        mode={mode}
        isTokenBasedBillingEnabled={tokenBasedBillingEnabled}
        autoPickedModel={autoPickedModel}
        autoModeDiscounts={autoModeDiscounts}
      />
      {pending?.command.action === 'requires-new-conversation' && (
        <NewConversationConfirmationDialog
          model={pending.model}
          reason={pending.command.reason}
          onDismiss={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'MODEL_PICKER_DIALOG_REQUIRES_NEW_CONVERSATION_CANCEL',
              mode: 'immersive',
              model: pending.model.id,
              type,
            })
            setPending(null)
          }}
          onSwitch={async () => {
            sendEvent('dotcom_chat.activate', {
              target: 'MODEL_PICKER_DIALOG_REQUIRES_NEW_CONVERSATION_CONFIRM',
              mode: 'immersive',
              model: pending.model.id,
              type,
            })
            setPending(null)
            await onNewThreadSelected()
            void manager.selectModel(pending.model)
          }}
        />
      )}
    </>
  )
}

interface ModelPickerDesignProps {
  selectedModel: CopilotChatModel
  models: CopilotChatModel[]
  onModelPicked: (m: CopilotChatModel) => void
  type: ModelPickerType
  disabled: boolean
  variant?: 'default' | 'invisible'
  showCategories?: boolean
  isTokenBasedBillingEnabled?: boolean
  showPreviewModelsSeparately?: boolean
  /** Chat mode for portal rendering - defaults to 'immersive' */
  mode?: CopilotChatMode
  /**
   * Model id the Auto router pinned for the active thread (from `thread.autoPickedModel`).
   * When set (and the chip is showing Auto), the chip and Auto row reveal this model's
   * display name on hover/focus, and the Auto entry's info popover surfaces its details.
   */
  autoPickedModel?: string
  /**
   * Discount ratios for the thread's Auto Mode suite. Stored here so the
   * downstream info popover can render discounted cost without another fetch.
   */
  autoModeDiscounts?: Record<string, number>
}

function getAutoPinnedModelDiscount(autoModeDiscounts: Record<string, number> | undefined, autoPinnedModelId?: string) {
  if (!autoModeDiscounts || !autoPinnedModelId) return undefined
  if (!Object.prototype.hasOwnProperty.call(autoModeDiscounts, autoPinnedModelId)) return 0

  const discount = autoModeDiscounts[autoPinnedModelId]
  return typeof discount === 'number' && Number.isFinite(discount) && discount >= 0 && discount <= 1
    ? discount
    : undefined
}

interface RetryButtonGroupProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
  disabled: boolean
  ref?: React.Ref<HTMLDivElement>
}

function RetryButtonGroup({ref, ...props}: RetryButtonGroupProps) {
  const {selectedModel, onModelPicked, disabled, ...buttonProps} = props
  return (
    <ButtonGroup className={styles.messageRetryButtonGroup} ref={ref}>
      <IconButton
        variant="invisible"
        aria-label={`Retry with ${selectedModel.displayName}`}
        onClick={() => onModelPicked(selectedModel)}
        icon={SyncIcon}
        disabled={disabled}
        className={styles.messageRetryButton}
      />
      <div className={styles.messageRetryDivider} />
      <IconButton
        variant="invisible"
        icon={TriangleDownIcon}
        aria-label="Retry with…"
        disabled={disabled}
        className={styles.messageRetryMenuButton}
        {...buttonProps}
      />
    </ButtonGroup>
  )
}

export function ModelPickerDesign({
  selectedModel,
  models,
  onModelPicked,
  type,
  disabled,
  variant = 'invisible',
  showCategories,
  isTokenBasedBillingEnabled = false,
  showPreviewModelsSeparately = true,
  mode = 'immersive',
  autoPickedModel,
  autoModeDiscounts,
}: ModelPickerDesignProps) {
  const [isOpen, setIsOpen] = useState(false)
  const preferredOverlaySide = useOverlayDirection()

  // If the resolved default is the Auto pseudo-entry (e.g. plan-aware fallback
  // from generateDefaultModelForPlan when CAPI omits is_chat_default) but the
  // backend list doesn't contain Auto, inject the selected model itself so
  // the menu matches the button.
  const augmentedModels =
    selectedModel.id === AUTO_MODEL_ID && !models.some(m => m.id === AUTO_MODEL_ID)
      ? [selectedModel, ...models]
      : models

  const shouldShowModelCategories = showCategories !== false && hasModelCategoryDiversity(augmentedModels)

  // Categorize models using the utility function
  const modelTiers = categorizeModels(augmentedModels, {
    shouldShowCategories: shouldShowModelCategories,
    shouldShowPreviewModelsSeparately: showPreviewModelsSeparately,
  })

  // Extract the categorized models for easier access
  const {
    lowTier: lowTierModels,
    midTier: midTierModels,
    highTier: highTierModels,
    base: baseModels,
    preview: previewModels,
    custom: customModelGroups,
  } = modelTiers

  // When backend curation (the `model_picker_enabled || is_upsell` filter in
  // toCopilotChatModels) leaves exactly one selectable model with the rest as
  // disabled upsell rows, surface the upgrade messaging in the picker header.
  // Global picker only.
  const isSingleEnabledModelPicker = type === 'global' && getOnlyEnabledModel(augmentedModels) !== null

  const hasCustomModels = Object.keys(customModelGroups).length > 0

  const autoPinnedModelId = autoPickedModel?.trim()
  // Resolve the Auto-pinned model object once so each rendered Auto row can show
  // it in the info popover. Only the Auto row consumes it (see renderModelItem).
  const autoPinnedModel = autoPinnedModelId ? augmentedModels.find(m => m.id === autoPinnedModelId) : undefined
  const autoPinnedModelDiscount = getAutoPinnedModelDiscount(autoModeDiscounts, autoPinnedModelId)

  const handleMenuOpen = useCallback(
    (open: boolean) => {
      // In case the retry divider is clicked
      const shouldOpen = !disabled && open
      setIsOpen(shouldOpen)

      if (shouldOpen) {
        sendEvent('dotcom_chat.activate', {
          target: 'MODEL_PICKER_OPEN',
          mode,
          model: selectedModel.id,
          type,
        })
      }
    },
    [disabled, mode, selectedModel.id, type],
  )

  const setVariant = variant ?? 'invisible'

  const componentRef = useRef<HTMLDivElement>(null)
  const [menuDirection, setMenuDirection] = useState<'up' | 'down'>('down')

  // When the thread has a pinned Auto model, reveal that model's display name on
  // hover/focus — both on the Auto chip and on the Auto row inside the menu. Resolve
  // the display name from the same model list the picker renders so names stay
  // consistent; fall back to the raw id when the pinned model is deprecated or no
  // longer in the user's list.
  const pinnedModelDisplayName =
    type === 'global' && autoPinnedModelId
      ? (models.find(m => m.id === autoPinnedModelId)?.displayName ?? autoPinnedModelId)
      : undefined
  // The chip only reveals the pinned model when Auto is the active selection.
  const chipPinnedModelDisplayName = selectedModel.id === AUTO_MODEL_ID ? pinnedModelDisplayName : undefined
  const chipPinnedModelDescription = chipPinnedModelDisplayName
    ? `Auto is currently using ${chipPinnedModelDisplayName}`
    : undefined

  // Each row gets the pinned model's display name (for the plain row tooltip) and the
  // Auto row additionally gets the resolved pinned model object (for the rich info
  // popover). Defined after pinnedModelDisplayName so both inputs are available.
  const renderModelItem = (m: CopilotChatModel) => (
    <ModelListItem
      key={m.id}
      model={m}
      selected={isSameModel(selectedModel, m) && type === 'global'}
      onModelPicked={onModelPicked}
      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
      autoPinnedModelDisplayName={pinnedModelDisplayName}
      autoPinnedModel={m.id === AUTO_MODEL_ID ? autoPinnedModel : undefined}
      autoPinnedModelDiscount={m.id === AUTO_MODEL_ID ? autoPinnedModelDiscount : undefined}
      autoModeDiscounts={m.id === AUTO_MODEL_ID ? autoModeDiscounts : undefined}
    />
  )

  const globalChipButton = (
    <ActionMenu.Button
      className={styles.menuButton}
      variant={setVariant}
      disabled={disabled}
      // Show the selected model's associated icon in the trigger — the Copilot mark
      // for Auto, the vendor/provider logo otherwise — using the same ModelVendorIcon
      // the menu rows render. The icon is decorative next to the visible model name
      // (the button already labels itself via the sr-only "Model:" + display name), so
      // pass `decorative` to suppress the vendor-logo alt text and avoid a redundant
      // screen-reader announcement.
      leadingVisual={() => <ModelVendorIcon model={selectedModel} decorative />}
    >
      <span className="sr-only">Model: </span>
      <div className={styles.buttonName}>{selectedModel.displayName}</div>
    </ActionMenu.Button>
  )

  return (
    <div ref={componentRef}>
      <ActionMenu open={isOpen} onOpenChange={handleMenuOpen}>
        {type === 'global' ? (
          chipPinnedModelDisplayName ? (
            // ActionMenu only threads its anchor props through a Primer Tooltip (TooltipV2)
            // wrapping ActionMenu.Button — it matches on that component's identity. A non-Primer
            // wrapper (e.g. pacer Tooltip) is not recognized as the anchor and breaks menu
            // positioning/ARIA, so this must stay Primer's Tooltip and remain a direct child.
            <PrimerTooltip
              text={`Auto (${chipPinnedModelDisplayName})`}
              type="description"
              aria-label={chipPinnedModelDescription}
              className={styles.pinnedModelTooltip}
            >
              {globalChipButton}
            </PrimerTooltip>
          ) : (
            globalChipButton
          )
        ) : (
          <ActionMenu.Anchor aria-label={'Retry with model'}>
            <RetryButtonGroup selectedModel={selectedModel} onModelPicked={onModelPicked} disabled={disabled} />
          </ActionMenu.Anchor>
        )}

        <ActionMenu.Overlay
          width={type === 'global' ? 'medium' : 'small'}
          side={preferredOverlaySide}
          overflow="auto"
          className={styles.actionMenuOverlay}
          maxHeight="fit-content"
          align="end"
          position={mode === 'assistive' ? 'relative' : undefined}
          portalContainerName={mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
          onPositionChange={({position}) => {
            const downPositions = ['inside-bottom', 'outside-bottom']
            setMenuDirection(downPositions.includes(position.anchorSide) ? 'down' : 'up')
          }}
        >
          <ActionList className={styles.actionList} selectionVariant={type === 'global' ? 'single' : undefined}>
            {type === 'message-retry' && menuDirection === 'down' && (
              <>
                <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
                <ActionList.Divider />
              </>
            )}
            {type === 'global' && <div className={styles.menuHeading}>Models</div>}
            {(copilotFeatureFlags.premiumRequestQuotasEnabled ||
              isTokenBasedBillingEnabled ||
              isSingleEnabledModelPicker) && (
              <ModelPickerHeader
                isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                hasSingleEnabledModel={isSingleEnabledModelPicker}
              />
            )}

            {baseModels.length > 0 && (
              <ActionList.Group>
                {type !== 'global' && <ActionList.GroupHeading>Models</ActionList.GroupHeading>}
                {baseModels.map(renderModelItem)}
                {(lowTierModels.length > 0 ||
                  midTierModels.length > 0 ||
                  highTierModels.length > 0 ||
                  previewModels.length > 0 ||
                  hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {lowTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Fast and cost-efficient</ActionList.GroupHeading>
                {lowTierModels.map(renderModelItem)}
                {(midTierModels.length > 0 || highTierModels.length > 0) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {midTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Versatile and highly intelligent</ActionList.GroupHeading>
                {midTierModels.map(renderModelItem)}
                {(highTierModels.length > 0 || previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {highTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Most powerful at complex tasks</ActionList.GroupHeading>
                {highTierModels.map(renderModelItem)}
                {(previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {previewModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Preview</ActionList.GroupHeading>
                {previewModels.map(renderModelItem)}
                {hasCustomModels && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {hasCustomModels && (
              <CustomModelActionListGroups customModelGroups={customModelGroups}>
                {renderModelItem}
              </CustomModelActionListGroups>
            )}

            {type === 'message-retry' && menuDirection === 'up' && (
              <>
                <ActionList.Divider />
                <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

function CustomModelActionListGroups({
  customModelGroups,
  children,
}: {
  customModelGroups: {[groupKey: string]: CustomModelGroup}
  children: (model: CopilotChatModel) => ReactNode
}) {
  const groupKeys = Object.keys(customModelGroups)
  const lastGroupIndex = groupKeys.length - 1

  return groupKeys.map((groupKey, i) => {
    const group = customModelGroups[groupKey]
    if (!group) return null
    return (
      <ActionList.Group key={groupKey}>
        <ActionList.GroupHeading>{group.displayName}</ActionList.GroupHeading>
        {group.models.map(children)}
        {i < lastGroupIndex && <ActionList.Divider />}
      </ActionList.Group>
    )
  })
}

interface RetryListItemProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
}

function RetryListItem({selectedModel, onModelPicked}: RetryListItemProps) {
  return (
    <ActionList.Item key={selectedModel.id} onSelect={() => onModelPicked(selectedModel)}>
      <ActionList.LeadingVisual>
        <SyncIcon />
      </ActionList.LeadingVisual>
      Try again
      <ActionList.Description variant="block" truncate>
        {selectedModel.displayName}
      </ActionList.Description>
    </ActionList.Item>
  )
}

interface ModelListItemProps {
  model: CopilotChatModel
  selected: boolean
  onModelPicked: (m: CopilotChatModel) => void
}

export function ModelListItem({
  model,
  selected,
  onModelPicked,
  isTokenBasedBillingEnabled = false,
  autoPinnedModelDisplayName,
  autoPinnedModel,
  autoPinnedModelDiscount,
  autoModeDiscounts,
}: ModelListItemProps & {
  isTokenBasedBillingEnabled?: boolean
  autoPinnedModelDisplayName?: string
  autoPinnedModel?: CopilotChatModel
  autoPinnedModelDiscount?: number
  autoModeDiscounts?: Record<string, number>
}) {
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled, canUpgradePlan} = useEntitlement()
  const itemRef = useRef<HTMLLIElement>(null)
  const freePlanAndOutOfQuota = plan === CopilotPlan.IndividualFree && chatQuotaExceeded
  const premiumModelAndOutOfQuota =
    model.billing?.is_premium &&
    plan !== CopilotPlan.IndividualFree &&
    premiumInteractionsQuotaExceeded &&
    !overagesEnabled
  const modelOutOfPlan = plan && model.billing?.restricted_to && !model.billing?.restricted_to.includes(plan)
  const isUpsellModel = model.is_upsell === true
  const minRequiredPlan = getMinimumRequiredPlan(model)
  const isProPlusModel = minRequiredPlan === CopilotPlan.IndividualProPlus
  const isMaxOnlyModel = minRequiredPlan === CopilotPlan.IndividualMax && copilotFeatureFlags.maxUpsellEnabled
  const locked = freePlanAndOutOfQuota || premiumModelAndOutOfQuota || modelOutOfPlan || isUpsellModel

  const lockedTooltipText = locked
    ? getLockedModelTooltip({
        isUpsellOrOutOfPlan: !!(isUpsellModel || modelOutOfPlan),
        freePlanAndOutOfQuota: !!freePlanAndOutOfQuota,
        premiumModelAndOutOfQuota: !!premiumModelAndOutOfQuota,
        isProPlusOnly: isProPlusModel,
        isMaxOnly: isMaxOnlyModel,
        canUpgradePlan,
      })
    : null

  // Render Multiplier when in Premium Request Unit (Auto pseudo-model has no real billing multiplier)
  const showMultiplierIndicators =
    model.id !== AUTO_MODEL_ID && !isCustomModel(model) && !isTokenBasedBillingEnabled && !isUpsellModel && !locked
  const multiplierLabelText = showMultiplierIndicators ? formatMultiplier(model.billing?.multiplier) : null

  // The model's info popover should be available only if the model has billable content to show (token prices or Auto's pinned model) and isn't locked
  const hasInfoContent =
    copilotFeatureFlags.modelPickerInfoPopover &&
    isTokenBasedBillingEnabled &&
    (!!model.billing?.token_prices || model.id === AUTO_MODEL_ID)

  // On the Auto row, reveal the thread's pinned model on hover/focus with descriptive
  // tooltip copy that also serves as the row's accessible description. When the rich info
  // popover is active for the Auto row (FF on + token billing), it already surfaces the
  // pinned model, so suppress this plain tooltip to avoid a double hover. The Auto row can
  // itself be locked (e.g. a free-plan user out of chat quota), in which case
  // lockedTooltipText takes priority — the quota message matters more than the pin reveal.
  const pinnedModelRowTooltip =
    model.id === AUTO_MODEL_ID && autoPinnedModelDisplayName && !hasInfoContent
      ? `Auto is currently using ${autoPinnedModelDisplayName}`
      : null
  // Bake the "Model locked." framing into the tooltip text so it doubles as the row's
  // accessible description: Pacer wires the tooltip text via aria-describedby, which wins
  // over a separate aria-description string. Locked takes priority over the pin reveal.
  const lockedRowTooltip = lockedTooltipText ? `Model locked. ${lockedTooltipText}` : null
  const rowTooltipText = lockedRowTooltip ?? pinnedModelRowTooltip

  const item = (
    <ActionList.Item
      key={model.id}
      onSelect={() => onModelPicked(model)}
      selected={selected}
      disabled={!!locked}
      aria-description={lockedRowTooltip ?? undefined}
      aria-describedby={isInfoOpen && hasInfoContent && !lockedTooltipText ? `model-info-${model.id}` : undefined}
      ref={itemRef}
      onMouseEnter={() => {
        if (hasInfoContent && !locked) setIsInfoOpen(true)
      }}
      onMouseLeave={e => {
        // Keep open if the mouse moved into the popover. relatedTarget is the
        // overlay container (an ancestor of the content div), so we must check
        // the AnchoredOverlay root rather than the content div itself.
        const popoverEl = document.getElementById(`model-info-${model.id}`)
        const overlayContainer = popoverEl?.closest('[data-component="AnchoredOverlay"]')
        if (overlayContainer?.contains(e.relatedTarget as Node)) return
        setIsInfoOpen(false)
      }}
      onFocus={e => {
        // Only show the popover when navigating between list items with the
        // keyboard (relatedTarget is another <li>). This excludes the
        // automatic focus Primer's FocusTrap applies when the menu opens,
        // which has no <li> predecessor.
        if (!(e.relatedTarget instanceof HTMLLIElement)) return
        if (hasInfoContent && !locked) setIsInfoOpen(true)
      }}
      onBlur={() => setIsInfoOpen(false)}
    >
      <ActionList.LeadingVisual>
        <ModelVendorIcon model={model} />
      </ActionList.LeadingVisual>
      <span className={clsx(locked && styles.modelNameDisabled)}>
        {model.displayName}
        {model.vendor && <VisuallyHidden>, {model.vendor} provider</VisuallyHidden>}
      </span>
      {locked && isMaxOnlyModel && <span className={styles.maxBadge}>Max</span>}
      {locked && isProPlusModel && <span className={styles.proPlusBadge}>Pro+</span>}
      {copilotFeatureFlags.premiumRequestQuotasEnabled && model.preview && model.id !== BASE_MODEL_ID && (
        <span className={styles.modelMetaLabel}>Preview</span>
      )}
      {showMultiplierIndicators && (
        <ActionList.TrailingVisual>
          <span className={styles.multiplier}>{multiplierLabelText}</span>
        </ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )

  const infoPopover =
    hasInfoContent && !locked ? (
      <ModelInfoPopover
        model={model}
        autoPinnedModel={autoPinnedModel}
        autoPinnedModelDisplayName={model.id === AUTO_MODEL_ID ? autoPinnedModelDisplayName : undefined}
        autoPinnedModelDiscount={autoPinnedModelDiscount}
        autoModeDiscounts={model.id === AUTO_MODEL_ID ? autoModeDiscounts : undefined}
        anchorRef={itemRef}
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    ) : null

  if (rowTooltipText) {
    return (
      <>
        <Tooltip text={rowTooltipText} position="top">
          {item}
        </Tooltip>
        {infoPopover}
      </>
    )
  }
  return (
    <>
      {item}
      {infoPopover}
    </>
  )
}

function getLockedModelTooltip({
  isUpsellOrOutOfPlan,
  freePlanAndOutOfQuota,
  premiumModelAndOutOfQuota,
  isProPlusOnly,
  isMaxOnly,
  canUpgradePlan,
}: {
  isUpsellOrOutOfPlan: boolean
  freePlanAndOutOfQuota: boolean
  premiumModelAndOutOfQuota: boolean
  isProPlusOnly: boolean
  isMaxOnly: boolean
  canUpgradePlan: boolean
}): string {
  if (freePlanAndOutOfQuota) return "You've used all free requests this month."
  if (premiumModelAndOutOfQuota) return "You've reached your premium request limit this month."
  const planName = isMaxOnly
    ? 'Copilot Max'
    : isProPlusOnly
      ? 'Copilot Pro+'
      : isUpsellOrOutOfPlan
        ? 'Copilot Pro'
        : null
  if (!planName) return 'This model is not available on your plan.'
  return canUpgradePlan ? `Upgrade to ${planName} to access this model` : `This model requires ${planName}`
}

function NewConversationConfirmationDialog({
  model,
  onDismiss,
  onSwitch,
  reason,
}: {
  model: CopilotChatModel
  onDismiss: () => void
  onSwitch: () => void
  reason: string
}) {
  return (
    <ConfirmationDialog
      title="Switch model"
      onClose={gesture => (gesture === 'confirm' ? onSwitch() : onDismiss())}
      confirmButtonContent="New conversation"
      confirmButtonType="primary"
    >
      {reason === 'external-content' && (
        <p>
          The {model.displayName} model currently doesn’t support external context from ongoing conversations. Start a
          new conversation to chat with this model.
        </p>
      )}
      {reason === 'degraded-image-support' && (
        <p>
          The {model.displayName} model doesn’t support answering questions about images. Start a new conversation
          without images to chat with this model.
        </p>
      )}
    </ConfirmationDialog>
  )
}

// `decorative` renders the icon without an accessible name (empty alt). Use it where the
// model is already named by adjacent visible/sr-only text — e.g. the trigger button — so
// the vendor logo isn't announced redundantly. Octicons are always aria-hidden regardless.
function ModelVendorIcon({model, decorative = false}: {model: CopilotChatModel; decorative?: boolean}) {
  // Auto is the GitHub Copilot pseudo-model; always show the Copilot mark even if a
  // CAPI-sourced Auto entry arrives carrying a vendor logoURL.
  if (model.id === AUTO_MODEL_ID) {
    return <CopilotIcon />
  }

  if (isCustomModel(model) && model.custom_model.provider) {
    return <ProviderLogo provider={model.custom_model.provider} />
  }

  return model.logoURL ? (
    <GitHubAvatar alt={decorative ? '' : `${model.vendor} logo`} square size={18} src={model.logoURL} />
  ) : (
    <CopilotIcon />
  )
}

function modelSwitchingRequiresNewConversationDueToExternalContent(
  messages: readonly CopilotChatMessage[],
  model: CopilotChatModel,
  onlyActiveMessages?: boolean,
): boolean {
  const messagesToCheck = onlyActiveMessages ? getActiveMessages(messages) : messages
  const hasFunctionCalls = messagesToCheck.some(message => message.skillExecutions?.length)

  return (
    hasFunctionCalls &&
    (!model.capabilities?.supports?.tool_calls ||
      model.isThirdParty ||
      // o1-ga supports tool calling, but CAPI gates it behind the
      // copilot_api_tools_for_non_streaming_models feature flag which is not yet fully rolled out.
      // Keep this guard until that flag is at 100%.
      model.capabilities.family === 'o1-ga')
  )
}

function modelSwitchingRequiresNewConversationDueToDegradedImageSupport(
  messages: readonly CopilotChatMessage[],
  currentReferences: CopilotChatReference[],
  model: CopilotChatModel,
  onlyActiveMessages?: boolean,
): boolean {
  const messagesToCheck = onlyActiveMessages ? getActiveMessages(messages) : messages
  const hasMediaContent =
    messagesToCheck.some(message => message.mediaContent?.length) || currentReferences.some(cr => cr.type === 'image')

  return hasMediaContent && !model.capabilities?.supports?.vision
}

function isSameModel(oldModel: CopilotChatModel, newModel: CopilotChatModel): boolean {
  return oldModel.id === newModel.id
}

type EntitlementSnapshot = ReturnType<typeof useEntitlement>

function ModelPickerHeader({
  isTokenBasedBillingEnabled = false,
  hasSingleEnabledModel = false,
}: {
  isTokenBasedBillingEnabled?: boolean
  hasSingleEnabledModel?: boolean
}) {
  const entitlement = useEntitlement()

  const content =
    isTokenBasedBillingEnabled || hasSingleEnabledModel
      ? renderUbbContent(entitlement, hasSingleEnabledModel)
      : renderLegacyContent(entitlement)

  return content ? <div className={styles.header}>{content}</div> : null
}

// UBB users see a per-SKU quota message when one applies. When no scenario
// applies, render nothing. The backend-curated picker doesn't surface a
// generic pricing-details link because the user can't freely choose among
// the models.
function renderUbbContent(entitlement: EntitlementSnapshot, hasSingleEnabledModel = false): ReactNode {
  const scenarioContent = renderUbbScenario(
    detectQuotaScenario(entitlement, UBB_THRESHOLDS),
    entitlement,
    hasSingleEnabledModel,
  )
  if (scenarioContent) return scenarioContent
  if (hasSingleEnabledModel && entitlement.canUpgradePlan) {
    return (
      <>
        <UpgradeLink url={UPGRADE_PLAN_URL_NEW} context="single-enabled-model" /> to access more models and higher
        limits.
      </>
    )
  }
  return null
}

function renderLegacyContent(entitlement: EntitlementSnapshot): ReactNode {
  return renderLegacyScenario(detectQuotaScenario(entitlement, LEGACY_THRESHOLDS), entitlement)
}

// Stable discriminator describing which footer scenario an Upgrade click came
// from, so conversion can be measured per surface without a brittle copy match.
// Derived from QuotaScenario (plus the single-enabled-model header link) rather than
// hand-coded strings.
type UpgradeLinkContext = QuotaScenario | 'single-enabled-model' | `${QuotaScenario}-single-enabled-model`

// Shared "Upgrade" link used by both the UBB and legacy footer paths. Fires a
// hydro click event before navigation (mirroring QuotaBanner) so the event lands
// even though the link unloads the page.
const UpgradeLink = ({context, url = UPGRADE_PLAN_URL}: {context: UpgradeLinkContext; url?: string}) => {
  const mode = useSafeChatState()?.mode ?? 'unknown'
  const plan = useEntitlement().plan ?? 'unknown'
  return (
    <Link
      inline
      href={url}
      onClick={() => {
        sendEvent('dotcom_chat.activate', {
          target: 'MODEL_PICKER_UPGRADE',
          mode,
          plan,
          context,
        })
      }}
    >
      Upgrade
    </Link>
  )
}

const ManageBillingLink = ({url = MANAGE_BILLING_URL}: {url?: string}) => (
  <Link inline href={url}>
    Manage billing
  </Link>
)

const EnableAdditionalLink = ({label, url = ENABLE_ADDITIONAL_REQUESTS_URL}: {label: string; url?: string}) => (
  <Link inline href={url}>
    {label}
  </Link>
)

/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
const AdminBold = ({label}: {label: string}) => <span className="font-weight-bold">{label} </span>

function formatUsagePercent(percentRemaining: number): number {
  // Mirror QuotaBanner: display the floor of the current usage percentage.
  return Math.floor(100 - percentRemaining)
}

// Centralised state machine shared by UBB and legacy renderers. Mapping plan
// + quota state to a single scenario keeps the control flow in one place and
// guarantees the two copy paths handle the same set of cases.
type QuotaScenario =
  | 'free-exceeded'
  | 'free-almost-out'
  | 'free-approaching'
  | 'free-default'
  | 'individual-overage-active'
  | 'individual-premium-exceeded'
  | 'individual-premium-approaching'
  | 'individual-default'
  | 'pro-plus-overage-active'
  | 'pro-plus-premium-exceeded'
  | 'pro-plus-premium-approaching'
  | 'max-overage-active'
  | 'max-premium-exceeded'
  | 'max-premium-approaching'
  | 'business-premium-exceeded-upgradeable'
  | 'business-premium-approaching-upgradeable'
  | 'business-premium-exceeded-locked'
  | 'business-premium-approaching-locked'
  | 'enterprise-premium-exceeded-purchasable'
  | 'enterprise-premium-exceeded-locked'
  | 'enterprise-premium-approaching-purchasable'
  | 'enterprise-premium-approaching-locked'

// Footer-level thresholds for the "approaching" and (optional) "almost-out"
// tiers. UBB uses the same values as `use-quota-banner.ts` so the picker
// footer and the banner trigger at identical quota levels; legacy uses the
// historical FREE_QUOTA_TRIGGER / PREMIUM_QUOTA_TRIGGER constants so its
// behaviour is unchanged from main.
interface QuotaThresholds {
  freeApproaching: number
  freeAlmostOut?: number
  premiumApproaching: number
}

const UBB_THRESHOLDS: QuotaThresholds = {
  freeApproaching: APPROACHING_THRESHOLD,
  freeAlmostOut: ALMOST_OUT_THRESHOLD,
  premiumApproaching: APPROACHING_THRESHOLD,
}

const LEGACY_THRESHOLDS: QuotaThresholds = {
  freeApproaching: FREE_QUOTA_TRIGGER,
  premiumApproaching: PREMIUM_QUOTA_TRIGGER,
}

function detectQuotaScenario(e: EntitlementSnapshot, thresholds: QuotaThresholds): QuotaScenario | null {
  const {
    plan,
    premiumChatQuotaRemaining,
    premiumInteractionsQuotaExceeded,
    chatQuotaRemaining,
    chatQuotaExceeded,
    canPurchaseAdditionalQuota,
    canUpgradePlan,
    overagesEnabled,
  } = e

  if (plan === CopilotPlan.IndividualFree) {
    if (chatQuotaExceeded) return 'free-exceeded'
    if (thresholds.freeAlmostOut !== undefined && chatQuotaRemaining <= thresholds.freeAlmostOut) {
      return 'free-almost-out'
    }
    if (chatQuotaRemaining <= thresholds.freeApproaching) return 'free-approaching'
    return 'free-default'
  }

  // checking student to maintain parity
  if (plan === CopilotPlan.IndividualPro || plan === CopilotPlan.IndividualStudent) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'individual-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'individual-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'individual-premium-approaching'
    }
    return 'individual-default'
  }

  if (plan === CopilotPlan.IndividualProPlus) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'pro-plus-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'pro-plus-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'pro-plus-premium-approaching'
    }
    return null
  }

  if (plan === CopilotPlan.IndividualMax) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'max-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'max-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'max-premium-approaching'
    }
    return null
  }

  if (plan === CopilotPlan.Business) {
    if (overagesEnabled) return null
    if (premiumInteractionsQuotaExceeded) {
      return canUpgradePlan ? 'business-premium-exceeded-upgradeable' : 'business-premium-exceeded-locked'
    }
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching) {
      return canUpgradePlan ? 'business-premium-approaching-upgradeable' : 'business-premium-approaching-locked'
    }
    return null
  }

  if (plan === CopilotPlan.Enterprise) {
    if (overagesEnabled) return null
    if (premiumInteractionsQuotaExceeded) {
      return canPurchaseAdditionalQuota
        ? 'enterprise-premium-exceeded-purchasable'
        : 'enterprise-premium-exceeded-locked'
    }
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching) {
      return canPurchaseAdditionalQuota
        ? 'enterprise-premium-approaching-purchasable'
        : 'enterprise-premium-approaching-locked'
    }
    return null
  }

  return null
}

// For UBB scenario we don't need 'exceeded' cases as the model picker is disabled/unclickable
function renderUbbScenario(
  scenario: QuotaScenario | null,
  e: EntitlementSnapshot,
  hasSingleEnabledModel = false,
): ReactNode {
  const upgradeLinkUrl = UPGRADE_PLAN_URL_NEW
  const upgradeSuffix = hasSingleEnabledModel ? ' to access more models and higher limits.' : ' to increase your limit.'
  // Telemetry discriminator for any Upgrade click in this path. When the picker
  // has a single enabled model the CTA is shown for a different reason, so we
  // compose that into the context. The 'single-enabled-model' fallback is never
  // used (null scenario renders no link) but keeps the value well-typed.
  const upgradeContext: UpgradeLinkContext =
    scenario === null ? 'single-enabled-model' : hasSingleEnabledModel ? `${scenario}-single-enabled-model` : scenario
  const enableAdditionalUrl = copilotFeatureFlags.ubbNewCtaDestinations
    ? ENABLE_ADDITIONAL_REQUESTS_URL
    : MANAGE_BUDGET_URL
  const resetDate = formatQuotaResetLabel(e.quotaResetAt)

  switch (scenario) {
    case 'free-almost-out':
      return (
        <>
          You&apos;re almost out of your included usage with the free plan.
          {e.canUpgradePlan && (
            <>
              {' '}
              <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} />
              {upgradeSuffix}
            </>
          )}
        </>
      )
    case 'free-approaching':
      return (
        <>
          You&apos;re approaching the limit of your included usage with the free plan.
          {e.canUpgradePlan && (
            <>
              {' '}
              <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} />
              {upgradeSuffix}
            </>
          )}
        </>
      )
    case 'free-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-overage-active':
    case 'pro-plus-overage-active':
    case 'max-overage-active':
      return (
        <>
          <span>
            {`You're now using additional usage until your included credits reset${resetDate ? ` ${resetDate}` : ''}.`}
          </span>{' '}
          <ManageBillingLink url={MANAGE_BUDGET_URL} />
        </>
      )
    case 'individual-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-premium-approaching':
    case 'pro-plus-premium-approaching':
    case 'max-premium-approaching':
      return e.canUpgradePlan ? (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to increase your limit or{' '}
          <EnableAdditionalLink label="enable additional usage" url={enableAdditionalUrl} /> to get more.
        </>
      ) : (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <EnableAdditionalLink label="Enable additional usage" url={enableAdditionalUrl} /> to get more.
        </>
      )
    case 'business-premium-approaching-upgradeable':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to increase your limit.
        </>
      )
    case 'business-premium-approaching-locked':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <AdminBold label="Ask your admin to upgrade" /> to increase your limit.
        </>
      )
    case 'enterprise-premium-approaching-purchasable':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <EnableAdditionalLink label="Enable additional usage" url={enableAdditionalUrl} /> to get more usage after the
          limit is reached.
        </>
      )
    case 'enterprise-premium-approaching-locked':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits. Ask your
          admin to <AdminBold label="enable additional usage" /> to get more usage.
        </>
      )
    case null:
      return null
    default:
      // For UBB, we shouldn't have any 'exceeded' scenarios because the picker is disabled when over quota. If we do hit one of those scenarios, or any other unexpected scenario, log it and return null so the footer doesn't render at all.
      return null
  }
}

function renderLegacyScenario(scenario: QuotaScenario | null, e: EntitlementSnapshot): ReactNode {
  // Telemetry discriminator for any Upgrade click in this path. The 'free-exceeded'
  // fallback is never used (null scenario renders no link) but keeps it well-typed.
  const upgradeContext: UpgradeLinkContext = scenario ?? 'free-exceeded'
  switch (scenario) {
    case 'free-exceeded':
      return e.canUpgradePlan ? (
        <>
          You have used all requests available this month. <UpgradeLink context={upgradeContext} />.
        </>
      ) : (
        <>You have used all requests available this month.</>
      )
    // Legacy thresholds (LEGACY_THRESHOLDS) never produce 'free-almost-out';
    // included for exhaustive switch coverage only.
    case 'free-almost-out':
      return null
    case 'free-approaching':
      return e.canUpgradePlan ? (
        <>
          You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month.{' '}
          <UpgradeLink context={upgradeContext} /> to increase your limit.
        </>
      ) : (
        <>You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month.</>
      )
    case 'free-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-overage-active':
    case 'pro-plus-overage-active':
      return (
        <>
          You have used all premium requests available this month. You will be charged per premium request until the
          limit resets on {e.resetDate}. <ManageBillingLink />
        </>
      )
    case 'individual-premium-exceeded':
      return e.canUpgradePlan ? (
        <>
          You have used all premium requests available this month. <UpgradeLink context={upgradeContext} /> to increase
          your limit. Limit resets on {e.resetDate}.
        </>
      ) : (
        <>You have used all premium requests available this month. Limit resets on {e.resetDate}.</>
      )
    case 'individual-premium-approaching':
      return e.canUpgradePlan ? (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <UpgradeLink context={upgradeContext} /> to increase your limit.
        </>
      ) : (
        <>You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.</>
      )
    case 'individual-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'pro-plus-premium-exceeded':
      return (
        <>
          You have used all premium requests available this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more. Limit resets on {e.resetDate}.
        </>
      )
    case 'pro-plus-premium-approaching':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more.
        </>
      )
    case 'business-premium-exceeded-upgradeable':
      return (
        <>
          You have used all premium requests available this month. <UpgradeLink context={upgradeContext} /> to increase
          your limit. Limit resets on {e.resetDate}.
        </>
      )
    case 'business-premium-approaching-upgradeable':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <UpgradeLink context={upgradeContext} /> to increase your limit.
        </>
      )
    case 'business-premium-exceeded-locked':
      return (
        <>
          You have reached your monthly limit for premium requests. <AdminBold label="Ask your admin to upgrade" /> to
          increase your limit. Limit resets on {e.resetDate}.
        </>
      )
    case 'business-premium-approaching-locked':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <AdminBold label="Ask your admin to upgrade" /> to increase your limit.
        </>
      )
    case 'enterprise-premium-exceeded-purchasable':
      return (
        <>
          You have used all premium requests available this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to increase your limit. Limit resets on{' '}
          {e.resetDate}.
        </>
      )
    case 'enterprise-premium-exceeded-locked':
      return (
        <>
          You have reached your monthly limit for premium requests. Ask your admin to{' '}
          <AdminBold label="enable additional requests" /> to get more usage. Limit resets on {e.resetDate}.
        </>
      )
    case 'enterprise-premium-approaching-purchasable':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more usage after the limit is reached.
        </>
      )
    case 'enterprise-premium-approaching-locked':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month. Ask your admin to{' '}
          <AdminBold label="enable additional requests" /> to get more usage.
        </>
      )
    case null:
      return null
  }
}
